<template>
  <div>
    <b-card
      no-body
      class="mb-0"
    >
      <div class="m-2">
        <h4 class="mb-0">
          <feather-icon
            icon="CornerUpLeftIcon"
            size="21"
            class="mr-50"
          />
          <span class="align-middle">Statistiques des Retours Définitifs par Fournisseur</span>
        </h4>
        <small class="text-muted">Analyse des retours définitifs basée sur les bons de retour</small>
      </div>
    </b-card>

    <b-card class="mt-2">
      <b-row>
        <b-col
          md="5"
          class="mb-1"
        >
          <label>Date de début</label>
          <b-form-input
            v-model="filters.startDate"
            type="date"
            :max="today"
          />
        </b-col>

        <b-col
          md="5"
          class="mb-1"
        >
          <label>Date de fin</label>
          <b-form-input
            v-model="filters.endDate"
            type="date"
            :max="today"
          />
        </b-col>

        <b-col
          md="2"
          class="mb-1 d-flex align-items-end"
        >
          <b-button
            variant="primary"
            block
            :disabled="isLoading"
            @click="fetchStats"
          >
            <feather-icon
              icon="SearchIcon"
              class="mr-50"
            />
            Rechercher
          </b-button>
        </b-col>
      </b-row>

      <b-row>
        <b-col md="12">
          <b-button
            variant="outline-secondary"
            size="sm"
            @click="resetFilters"
          >
            <feather-icon
              icon="RotateCcwIcon"
              size="14"
              class="mr-25"
            />
            Réinitialiser
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <div
      v-if="isLoading"
      class="text-center my-5"
    >
      <b-spinner
        variant="primary"
        label="Chargement..."
      />
      <p class="mt-2">
        Chargement des statistiques...
      </p>
    </div>

    <b-alert
      v-if="error"
      variant="danger"
      show
      dismissible
      class="mt-2"
      @dismissed="error = null"
    >
      <div class="alert-body">
        <feather-icon
          icon="AlertCircleIcon"
          class="mr-50"
        />
        <span>{{ error }}</span>
      </div>
    </b-alert>

    <div v-if="!isLoading && summary && suppliers.length > 0">
      <b-row class="mt-2">
        <b-col
          lg="4"
          md="6"
          sm="12"
        >
          <b-card class="stat-card">
            <div class="d-flex justify-content-between">
              <div>
                <p class="stat-label">
                  Nombre de Fournisseurs
                </p>
                <h3 class="stat-value text-primary">
                  {{ summary.total_suppliers }}
                </h3>
              </div>
              <div class="stat-icon bg-light-primary">
                <feather-icon
                  icon="UsersIcon"
                  size="24"
                />
              </div>
            </div>
          </b-card>
        </b-col>

        <b-col
          lg="4"
          md="6"
          sm="12"
        >
          <b-card class="stat-card">
            <div class="d-flex justify-content-between">
              <div>
                <p class="stat-label">
                  Retours Définitifs
                </p>
                <h3 class="stat-value text-danger">
                  {{ summary.total_retour_definitif_orders }}
                </h3>
              </div>
              <div class="stat-icon bg-light-danger">
                <feather-icon
                  icon="CornerUpLeftIcon"
                  size="24"
                />
              </div>
            </div>
          </b-card>
        </b-col>

        <b-col
          lg="4"
          md="6"
          sm="12"
        >
          <b-card class="stat-card">
            <div class="d-flex justify-content-between">
              <div>
                <p class="stat-label">
                  Total Frais de Retour
                </p>
                <h3 class="stat-value text-warning">
                  {{ formatCurrency(summary.total_return_price) }}
                </h3>
              </div>
              <div class="stat-icon bg-light-warning">
                <feather-icon
                  icon="DollarSignIcon"
                  size="24"
                />
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <b-card
        v-if="suppliers.length > 0"
        title="Détails par Fournisseur"
      >
        <b-row class="mb-2">
          <b-col md="6">
            <b-form-input
              v-model="searchTerm"
              placeholder="Rechercher un fournisseur..."
              type="text"
            >
              <template #prepend>
                <feather-icon icon="SearchIcon" />
              </template>
            </b-form-input>
          </b-col>
          <b-col
            md="6"
            class="text-right"
          >
            <b-button
              variant="success"
              @click="exportToCSV"
            >
              <feather-icon
                icon="DownloadIcon"
                class="mr-50"
              />
              Exporter CSV
            </b-button>
          </b-col>
        </b-row>

        <b-table
          :items="paginatedSuppliers"
          :fields="tableFields"
          responsive
          striped
          hover
          :busy="isLoading"
          show-empty
          empty-text="Aucune donnée disponible"
        >
          <template #cell(supplier_name)="data">
            <div class="d-flex align-items-center">
              <b-avatar
                :text="getInitials(data.item.supplier_name)"
                variant="light-primary"
                size="32"
                class="mr-1"
              />
              <span class="font-weight-bold">{{ data.item.supplier_name }}</span>
            </div>
          </template>

          <template #cell(orders_retour_definitif_count)="data">
            <b-badge
              variant="light-danger"
              pill
              class="font-weight-bold"
            >
              {{ data.item.orders_retour_definitif_count }}
            </b-badge>
          </template>

          <template #cell(voucher_ids)="data">
            <div class="d-flex flex-wrap gap-1">
              <b-badge
                v-for="voucherId in data.item.voucher_ids"
                :key="voucherId"
                variant="light-info"
                class="mr-1 mb-1"
              >
                #{{ voucherId }}
              </b-badge>
            </div>
          </template>

          <template #cell(total_return_price)="data">
            <span class="font-weight-bold text-warning">
              {{ formatCurrency(data.item.total_return_price) }}
            </span>
          </template>

          <template #cell(avg_return_price)="data">
            <span class="text-muted">
              {{ formatCurrency(data.item.orders_retour_definitif_count > 0 ? data.item.total_return_price / data.item.orders_retour_definitif_count : 0) }}
            </span>
          </template>
        </b-table>

        <b-pagination
          v-model="currentPage"
          :total-rows="filteredSuppliers.length"
          :per-page="perPage"
          align="center"
          class="mt-2"
        />
      </b-card>
    </div>

    <b-card
      v-if="!isLoading && suppliers.length === 0 && !error && summary"
      class="mt-2"
    >
      <div class="text-center py-5">
        <feather-icon
          icon="InboxIcon"
          size="48"
          class="text-muted mb-2"
        />
        <h5 class="text-muted">
          Aucune donnée trouvée
        </h5>
        <p class="text-muted">
          Aucun retour définitif trouvé pour la période sélectionnée
        </p>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BPagination,
  BBadge,
  BSpinner,
  BAlert,
  BAvatar,
} from 'bootstrap-vue'
import axios from 'axios'

export default {
  name: 'ReturnVoucherStats',
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BPagination,
    BBadge,
    BSpinner,
    BAlert,
    BAvatar,
  },
  data() {
    return {
      filters: {
        startDate: '',
        endDate: '',
      },
      suppliers: [],
      summary: null,
      isLoading: false,
      error: null,
      searchTerm: '',
      currentPage: 1,
      perPage: 10,
      tableFields: [
        {
          key: 'supplier_name',
          label: 'Fournisseur',
          sortable: true,
        },
        {
          key: 'orders_retour_definitif_count',
          label: 'Retours Définitifs',
          sortable: true,
          tdClass: 'text-center',
        },
        {
          key: 'voucher_ids',
          label: 'Bons de Retour',
          sortable: false,
        },
        {
          key: 'total_return_price',
          label: 'Total Frais de Retour',
          sortable: true,
        },
        {
          key: 'avg_return_price',
          label: 'Frais Moyen par Colis',
          sortable: true,
        },
      ],
    }
  },
  computed: {
    today() {
      return new Date().toISOString().split('T')[0]
    },
    filteredSuppliers() {
      if (!this.searchTerm) {
        return this.suppliers
      }
      const term = this.searchTerm.toLowerCase()
      return this.suppliers.filter(supplier => supplier.supplier_name.toLowerCase().includes(term))
    },
    paginatedSuppliers() {
      const start = (this.currentPage - 1) * this.perPage
      const end = start + this.perPage
      return this.filteredSuppliers.slice(start, end)
    },
  },
  mounted() {
    this.setDefaultDates()
  },
  methods: {
    setDefaultDates() {
      const today = new Date()
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      [this.filters.startDate] = firstDayOfMonth.toISOString().split('T');
      [this.filters.endDate] = today.toISOString().split('T')
    },
    resetFilters() {
      this.setDefaultDates()
      this.suppliers = []
      this.summary = null
      this.error = null
      this.searchTerm = ''
      this.currentPage = 1
    },
    async fetchStats() {
      if (!this.filters.startDate || !this.filters.endDate) {
        this.error = 'Veuillez sélectionner les dates de début et de fin'
        return
      }

      try {
        this.isLoading = true
        this.error = null

        const response = await axios.get('/api/return-vouchers/stats-by-supplier/', {
          params: {
            start_date: this.filters.startDate,
            end_date: this.filters.endDate,
          },
        })

        this.suppliers = response.data.suppliers || []
        this.summary = response.data.summary || null
        this.currentPage = 1
      } catch (error) {
        console.error('Error fetching return voucher stats:', error)
        this.error = error.response?.data?.error || 'Une erreur est survenue lors du chargement des données'

        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Erreur',
            text: this.error,
            variant: 'danger',
            icon: 'AlertCircleIcon',
          },
        })
      } finally {
        this.isLoading = false
      }
    },
    formatCurrency(value) {
      if (!value) return '0.000 DT'
      return `${parseFloat(value).toFixed(3)} DT`
    },
    getInitials(name) {
      if (!name) return '??'
      const parts = name.split(' ')
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    },
    exportToCSV() {
      const headers = ['Fournisseur', 'Retours Définitifs', 'Total Frais de Retour', 'Frais Moyen']
      const rows = this.filteredSuppliers.map(supplier => [
        supplier.supplier_name,
        supplier.orders_retour_definitif_count,
        supplier.total_return_price.toFixed(3),
        (supplier.orders_retour_definitif_count > 0 ? supplier.total_return_price / supplier.orders_retour_definitif_count : 0).toFixed(3),
      ])

      let csvContent = 'data:text/csv;charset=utf-8,'
      csvContent += `${headers.join(',')}\n`
      rows.forEach(row => {
        csvContent += `${row.join(',')}\n`
      })

      const encodedUri = encodeURI(csvContent)
      const link = document.createElement('a')
      link.setAttribute('href', encodedUri)
      link.setAttribute('download', `retours_definitifs_par_fournisseur_${this.filters.startDate}_${this.filters.endDate}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      this.$toast({
        component: 'ToastificationContent',
        props: {
          title: 'Succès',
          text: 'Export CSV réussi',
          variant: 'success',
          icon: 'DownloadIcon',
        },
      })
    },
  },
}
</script>

<style scoped>
.stat-card {
  transition: transform 0.2s;
}

.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 25px 0 rgba(0, 0, 0, 0.1);
}

.stat-label {
  font-size: 0.857rem;
  font-weight: 500;
  color: #6e6b7b;
  margin-bottom: 0.5rem;
}

.stat-value {
  font-size: 1.714rem;
  font-weight: 600;
  margin-bottom: 0;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 0.357rem;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
